import { useCallback, useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  selectSelectedClient,
  selectSelectedCompany
} from "@/store/features/companies/companies";
import {
  clearClientMembers,
  clearCompanyMembers,
  loadClientMembers,
  loadCompanyMembers,
  selectActiveClientId,
  selectActiveCompanyId,
  selectAllClientMembersLoaded,
  selectAllCompanyMembersLoaded,
  selectAllMembers,
  selectClientMembers,
  selectClientMembersError,
  selectClientMembersLoading,
  selectClientMembersSearchesFetched,
  selectCompanyMembers,
  selectCompanyMembersError,
  selectCompanyMembersLoading,
  selectCompanyMembersSearchesFetched,
  selectMembersLoading
} from "@/store/features/companyMembers/companyMembers";

export const useCompanyMembers = () => {
  const dispatch = useDispatch();
  const lastDispatchedCompanyId = useRef<string | null>(null);
  const lastDispatchedClientId = useRef<string | null>(null);

  const companyMembers = useSelector(selectCompanyMembers);
  const clientMembers = useSelector(selectClientMembers);
  const allMembers = useSelector(selectAllMembers);
  const isLoading = useSelector(selectMembersLoading);
  const isCompanyMembersLoading = useSelector(selectCompanyMembersLoading);
  const isClientMembersLoading = useSelector(selectClientMembersLoading);
  const error = useSelector(selectCompanyMembersError);
  const clientMembersError = useSelector(selectClientMembersError);
  const storedActiveCompanyId = useSelector(selectActiveCompanyId);
  const storedActiveClientId = useSelector(selectActiveClientId);
  const companyMembersSearchesFetched = useSelector(
    selectCompanyMembersSearchesFetched
  );
  const clientMembersSearchesFetched = useSelector(
    selectClientMembersSearchesFetched
  );
  const allCompanyMembersLoaded = useSelector(selectAllCompanyMembersLoaded);
  const allClientMembersLoaded = useSelector(selectAllClientMembersLoaded);

  const activeClient = useSelector(selectSelectedClient);
  const activeCompany = useSelector(selectSelectedCompany);

  const loadCompanyMembersCallback = useCallback(
    (companyId: string, search?: string) => {
      const alreadyLoaded =
        storedActiveCompanyId === companyId &&
        (allCompanyMembersLoaded ||
          (search && companyMembersSearchesFetched.has(search)));
      if (alreadyLoaded) {
        return;
      }
      dispatch(loadCompanyMembers(companyId, search));
    },
    [
      dispatch,
      companyMembersSearchesFetched,
      allCompanyMembersLoaded,
      storedActiveCompanyId
    ]
  );

  const loadClientMembersCallback = useCallback(
    (clientId: string, search?: string) => {
      const alreadyLoaded =
        storedActiveClientId === clientId &&
        (allClientMembersLoaded ||
          (search && clientMembersSearchesFetched.has(search)));
      if (alreadyLoaded) {
        return;
      }
      dispatch(loadClientMembers(clientId, search));
    },
    [
      dispatch,
      clientMembersSearchesFetched,
      allClientMembersLoaded,
      storedActiveClientId
    ]
  );

  useEffect(() => {
    if (
      activeClient &&
      !isClientMembersLoading &&
      storedActiveClientId !== activeClient.id &&
      lastDispatchedClientId.current !== activeClient.id
    ) {
      lastDispatchedClientId.current = activeClient.id;
      dispatch(clearClientMembers());
      loadClientMembersCallback(activeClient.id);
    }

    if (
      activeCompany &&
      activeCompany.id !== activeClient?.id &&
      !isCompanyMembersLoading &&
      storedActiveCompanyId !== activeCompany.id &&
      lastDispatchedCompanyId.current !== activeCompany.id
    ) {
      lastDispatchedCompanyId.current = activeCompany.id;
      dispatch(clearCompanyMembers());
      loadCompanyMembersCallback(activeCompany.id);
    }
  }, [
    dispatch,
    activeCompany,
    loadCompanyMembersCallback,
    isCompanyMembersLoading,
    storedActiveCompanyId,
    activeClient,
    loadClientMembersCallback,
    isClientMembersLoading,
    storedActiveClientId
  ]);

  return {
    companyMembers,
    clientMembers,
    allMembers,
    isLoading,
    isCompanyMembersLoading,
    isClientMembersLoading,
    error,
    clientMembersError,
    activeCompanyId: storedActiveCompanyId,
    activeClientId: storedActiveClientId,
    loadMembers: loadCompanyMembersCallback,
    loadClientMembers: loadClientMembersCallback
  };
};
